import difference from 'lodash/difference';
import Router from 'next/router';
import { getSearchUrlWithFilters } from '@bridebook/toolbox';
import { fetchy } from '@bridebook/web/lib/fetchy';
import { CountryCodes, Market } from '@bridebook/toolbox/src/gazetteer';
import { TGetSearchUrlWithFiltersParams } from '@bridebook/toolbox/src/getSearchUrlWithFilters';
import { countriesExcludedTypes } from '@bridebook/toolbox/src/supplier/constants';
import type { Slug } from '@bridebook/toolbox/src/types';
import { selectMarketWithFallback } from '@bridebook/web/lib/i18n/selectors';
import {
  getPopularVenueTypesTexts,
  orderSupplierTypesByBookingPriority,
} from '@bridebook/web/lib/search-landing/utils';
import { setPopularVenueTypes, setSearchSource } from '@bridebook/web/lib/search/actions';
import { extractAreaParams, isSearchResultsPage } from '@bridebook/web/lib/search/utils';
import { navigateToRecentSearch } from '@bridebook/web/lib/search/utils/navigate-to-recent-search';
import { getBookedSupplierCategories } from '@bridebook/web/lib/shortlist/selectors';
import { recentLocationSearchesStorage } from '@bridebook/web/lib/storage-manager/hooks';
import { IDeps, ISearchSource } from '@bridebook/web/lib/types';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { noopAction } from '@bridebook/web/lib/utils';
import { assertState } from '@bridebook/web/lib/utils/assertState';
import { POPULAR_VENUE_TYPES } from './constants';

/**
 * The below function should only be called for logged-in users
 * as the global locations page is not available for logged-out
 * */
const navigateToGlobalLocations = () => () => {
  const asyncAction = async () => {
    await Router.push(UrlHelper.globalLocations);
  };
  asyncAction();

  return noopAction();
};

/*
When clicking on “Venues and Suppliers” on desktop or the “Explore” tab on mobile
- If there are recent searches, we open the recent search (from local storage)
- If there are no recent searches, we open the search for the wedding location
    - The order of suppliers is venue (until booked), then photographer, then florists (for markets with full directory)
    - The order of suppliers is venue (even if booked, for markets with venue directory)
 */
export const navigateToVenuesAndSuppliers =
  ({
    searchSource = 'navigationBar',
    navigateToRecent = true,
  }: { searchSource?: ISearchSource; navigateToRecent?: boolean } = {}) =>
  ({ getState, dispatch }: IDeps) => {
    const asyncAction = async () => {
      const state = getState();

      if (isSearchResultsPage(Router.asPath)) return;

      const recentSearches = navigateToRecent && recentLocationSearchesStorage.get();
      if (recentSearches && recentSearches.length > 0) {
        await navigateToRecentSearch(recentSearches[0]);
      } else {
        dispatch(setSearchSource(searchSource));

        const market = selectMarketWithFallback(state);
        if (!market.hasAnyDirectory) {
          dispatch(navigateToGlobalLocations());
          return;
        }

        const bookedSupplierCategories = getBookedSupplierCategories(state);
        const supplierTypesByBookingPriority = market.suppliers;
        if (!bookedSupplierCategories) {
          dispatch(
            navigateToSearchResults({
              slug: 'venue',
            }),
          );
          return;
        } else {
          const nextCategoryToBook = orderSupplierTypesByBookingPriority(
            difference(supplierTypesByBookingPriority, bookedSupplierCategories) || [],
          );
          dispatch(
            navigateToSearchResults({
              slug: nextCategoryToBook[0] ?? 'venue',
            }),
          );
          return;
        }
      }
    };
    asyncAction();
    return noopAction();
  };

export const navigateToSearchResults =
  (
    params: Partial<Omit<TGetSearchUrlWithFiltersParams, 'slug'>> & {
      placeId?: string;
      slug?: Slug;
      country?: CountryCodes;
    },
  ) =>
  ({ getState }: IDeps) => {
    const asyncAction = async () => {
      const state = getState();
      assertState(state.search.searchLocation, 'initialized', 'search');
      let as = '';

      if (params.area) {
        as = getSearchUrlWithFilters({
          area: params.area,
          placeId: params.placeId,
          filters: params.filters ?? {},
          as: true,
          ...params,
          // Placed after the spread so an explicit `slug: undefined` in params can't defeat the fallback
          slug: params.slug ?? 'venue',
          areaContext: params.areaContext
            ? params.areaContext
            : params.country
              ? [params.country]
              : [],
        });
      } else {
        const { location, country } = extractAreaParams(state.search.searchLocation.selected.area);
        const { placeId } = state.search.searchLocation.selected;
        as = getSearchUrlWithFilters({
          area: location,
          placeId,
          filters: {},
          as: true,
          ...params,
          // Placed after the spread so an explicit `slug: undefined` in params can't defeat the fallback
          slug: params.slug ?? 'venue',
          areaContext: country
            ? [country]
            : state.search.searchLocation.selected.countryCode
              ? [state.search.searchLocation.selected.countryCode]
              : [],
        });
      }

      await Router.push(as, undefined, { shallow: false }).then(() => window.scrollTo(0, 0));
    };
    asyncAction();

    return noopAction();
  };

export const fetchPopularVenueTypes =
  (market: Market) =>
  ({ getState, dispatch }: IDeps) => {
    const asyncAction = async () => {
      const state = getState();
      const { searchLocation, popularVenueTypes } = state.search;
      assertState(searchLocation, 'initialized', 'search');
      const searchLocationArea = searchLocation.selected.area;

      // If the location is the same, no need to request again
      if (searchLocationArea === popularVenueTypes?.location) return;

      dispatch(
        setPopularVenueTypes({
          list: [],
          isPending: true,
          location: searchLocationArea,
        }),
      );

      const area = searchLocation.selected.area || market.getCountryName();

      const result = await fetchy()
        .authenticated.post('/api/search/supplier-types', null, {
          area,
          country: market.getCountryName(),
          countryCode: market.country,
          locationInfo: true,
          searchParams: ['wedding-venues', area],
          type: 'venue',
        })
        .catch(() => null);

      // If there was another search triggered while waiting for the response
      // of the supplierTypes, don't save the data of the initial response
      const anotherSearchLocation = getState().search.searchLocation;
      assertState(anotherSearchLocation, 'initialized', 'search');
      if (!result || searchLocationArea !== anotherSearchLocation.selected.area) return;

      const texts = getPopularVenueTypesTexts();

      const venueTypes = Object.keys(result)
        .reduce<string[]>((acc, venueType) => {
          acc.push(venueType);
          return acc;
        }, [])
        .filter(
          (venueType) =>
            result[venueType] > 0 &&
            !!texts[venueType] &&
            countriesExcludedTypes[market.country]?.has(venueType) !== true &&
            POPULAR_VENUE_TYPES.venueTypesImgs.has(venueType),
        );

      const isValidList = venueTypes.length >= POPULAR_VENUE_TYPES.minCards;

      if (isValidList) {
        venueTypes.sort((a, b) => result[b] - result[a]);
      }

      dispatch(
        setPopularVenueTypes({
          list: isValidList ? venueTypes.slice(0, POPULAR_VENUE_TYPES.maxCards) : [],
          isPending: false,
          location: searchLocationArea,
        }),
      );
    };

    asyncAction();

    return noopAction();
  };
